import { useEffect, useState, useRef, useCallback } from "react";

import { useDebounce } from "../../../../hooks/useDebounce";
import { Transaction, TransactionChannelMap, TransactionStatusMap } from "../../../../model/transaction";
import {
	useLazyGetPostpaidTransactionsForUtilityAdminQuery,
	useLazyExportPostpaidTransactionsForUtilityAdminQuery,
} from "../../../../service/transaction-service";
import { getErrorMessage } from "../../../../utils/error-handler";
import styles from "../../utilityAdmin.module.scss";
import { formatCurrency, formatTimestamp } from "../../../../utils";
import { EyeIcon, FilterIcon2, PostpaidActiveIcon } from "../../../../assets/icons";
import CommonSideDialog from "../../../../common/components/side-dialog/CommonSideDialog";
import { FilterItem } from "../../../../common/components/xepp/table-filter/table-filter.type";
import DataTable, { ColumnType } from "../../../../common/components/xepp/table/DataTable";
import Button from "../../../../common/components/xepp/button/Button";
import SearchInput from "../../../../common/components/xepp/search-input/SearchInput";
import Filter from "../../../../common/components/xepp/table-filter/table-filter-components";
import TransactionDetailsContainer from "../../../../common/components/xepp/transaction-details/TransactionDetailsContainer";
import EmptyState from "../../../../common/components/xepp/empty-state/EmptyState";
import { TablePagination } from "../../../../common/components/xepp/table-pagination/TablePagination";
import { Range } from "react-date-range";
import { hasPermission } from "../../../../guards/common/authentification-functions";
import { UtilityPermissions } from "../../../../model/permissions/permissions";
import { toast } from "react-toastify";
import { useConfigLayout } from "../../../../hooks/useConfigLayout";

const formatLocalDate = (date: Date): string => {
	const pad = (n: number) => String(n).padStart(2, "0");
	return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};
export const PostpaidHistory = () => {
	const [searchTerm, setSearchTerm] = useState("");
	const debouncedSearchTerm = useDebounce(searchTerm, 500);
	const [isOpen, setIsOpen] = useState(false);
	const [selectedReference, setSelectedReference] = useState<string>("");
	const [isFilterOpen, setIsFilterOpen] = useState(false);
	const [selectedPaymentStatus, setSelectedPaymentStatus] = useState<string>("");
	const [selectedBillingStatus, setSelectedBillingStatus] = useState<boolean | null>(null);
	const [selectedTransactionType, setSelectedTransactionType] = useState<string>("");

	const now = new Date();
	const startDate = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);

	const [dateRange, setDateRange] = useState<Range>({
		startDate: startDate,
		endDate: new Date(),
		key: "selection",
	});
	const [appliedFilters, setAppliedFilters] = useState<{
		PaymentStatus: string;
		BillingStatus?: boolean | null;
		TransactionType: string;
		from?: string;
		to?: string;
	}>({
		PaymentStatus: "",
		BillingStatus: null,
		TransactionType: "",
		from: formatLocalDate(startDate),
		to: formatLocalDate(new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1)),
	});
	const { updateLayout } = useConfigLayout();
	const [getPostPaidTransactions, { data, isLoading, isFetching }] = useLazyGetPostpaidTransactionsForUtilityAdminQuery();
	const [exportPostpaidTransactions] = useLazyExportPostpaidTransactionsForUtilityAdminQuery();

	const [Page, setPage] = useState(1);
	const PageSize = 15;

	const { count } = data ?? {};

	const appliedFiltersRef = useRef(appliedFilters);
	const searchTermRef = useRef(debouncedSearchTerm);
	useEffect(() => {
		appliedFiltersRef.current = appliedFilters;
	}, [appliedFilters]);
	useEffect(() => {
		searchTermRef.current = debouncedSearchTerm;
	}, [debouncedSearchTerm]);

	const handleExport = useCallback(() => {
		const filters = appliedFiltersRef.current;
		const search = searchTermRef.current;
		const exportParams = {
			Page: 1,
			PageSize: 0,
			...(filters.PaymentStatus && { PaymentStatus: filters.PaymentStatus }),
			...(filters.BillingStatus !== null && { BillingStatus: filters.BillingStatus }),
			...(filters.TransactionType && { TransactionType: filters.TransactionType }),
			...(filters.from && { from: filters.from }),
			...(filters.to && { to: filters.to }),
			...(search && { search }),
		};
		toast.info("Preparing export...");
		exportPostpaidTransactions(exportParams)
			.unwrap()
			.then(blob => {
				const url = URL.createObjectURL(blob);
				const link = document.createElement("a");
				link.href = url;
				link.download = `postpaid-transactions-${new Date().toISOString().slice(0, 10)}.csv`;
				document.body.appendChild(link);
				link.click();
				document.body.removeChild(link);
				URL.revokeObjectURL(url);
			})
			.catch((err: any) => {
				toast.error(getErrorMessage(err?.data));
			});
	}, [exportPostpaidTransactions]);

	useEffect(() => {
		updateLayout({ exportHandler: handleExport });
		return () => {
			updateLayout({ exportHandler: null });
		};
	}, [handleExport, updateLayout]);

	useEffect(() => {
		const filterParams = {
			Page,
			PageSize,
			...(appliedFilters.PaymentStatus && { PaymentStatus: appliedFilters.PaymentStatus }),
			...(appliedFilters.BillingStatus !== null && { BillingStatus: appliedFilters.BillingStatus }),
			...(appliedFilters.TransactionType && { TransactionType: appliedFilters.TransactionType }),
			...(appliedFilters.from && { from: appliedFilters.from }),
			...(appliedFilters.to && { to: appliedFilters.to }),
			...(debouncedSearchTerm && { search: debouncedSearchTerm }),
		};

		getPostPaidTransactions(filterParams)
			.unwrap()
			.catch(err => getErrorMessage(err?.data));
		console.log(data);
	}, [data, Page, PageSize, appliedFilters, debouncedSearchTerm, getPostPaidTransactions]);

	useEffect(() => {
		if (searchTerm !== debouncedSearchTerm && searchTerm !== "") {
			setPage(1);
		}
	}, [searchTerm, debouncedSearchTerm]);

	const handleOpenFilter = () => {
		setIsFilterOpen(true);
	};

	const handleCloseFilter = () => {
		setIsFilterOpen(false);
	};

	const handleApplyFilter = () => {
		let endDate = dateRange.endDate;
		if (dateRange.startDate && endDate && dateRange.startDate.toDateString() === endDate.toDateString()) {
			endDate = new Date(endDate);
			endDate.setDate(endDate.getDate() + 1);
		}
		setAppliedFilters({
			PaymentStatus: selectedPaymentStatus,
			BillingStatus: selectedBillingStatus,
			TransactionType: selectedTransactionType,
			from: dateRange.startDate ? formatLocalDate(dateRange.startDate) : undefined,
			to: endDate ? formatLocalDate(new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate() + 1)) : undefined,
		});

		setIsFilterOpen(false);
		setPage(1);
	};

	const handleResetFilter = () => {
		setSelectedPaymentStatus("");
		setSelectedBillingStatus(null);
		setSelectedTransactionType("");
		setIsFilterOpen(false);

		setAppliedFilters({
			PaymentStatus: "",
			BillingStatus: null,
			TransactionType: "",
			from: formatLocalDate(startDate),
			to: formatLocalDate(new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1)),
		});

		setPage(1);
	};

	const handlePaymentStatusChange = (value: string) => {
		setSelectedPaymentStatus(value);
	};

	const handleBillingStatusChange = (value: string) => {
		if (value === "notified") {
			setSelectedBillingStatus(true);
		} else if (value === "not-notified") {
			setSelectedBillingStatus(false);
		} else {
			setSelectedBillingStatus(null);
		}
	};

	const handleTransactionTypeChange = (value: string) => {
		setSelectedTransactionType(value);
	};

	const getBillingStatusValue = () => {
		if (selectedBillingStatus === true) return "notified";
		if (selectedBillingStatus === false) return "not-notified";
		return "";
	};

	const handleDateRangeChange = (ranges: any) => {
		setDateRange(ranges.selection);
	};

	const filterItems: FilterItem[] = [
		{
			id: "date_range",
			label: "DATE RANGE",
			type: "date",
			initialDateRange: dateRange,
			onChange: handleDateRangeChange,
		},
		{
			id: "payment_status",
			label: "PAYMENT STATUS",
			type: "radio",
			options: [
				{
					id: "In-Progress",
					label: "In-Progress",
					value: "In-Progress",
					isSelected: selectedPaymentStatus === "In-Progress",
				},
				{
					id: "failed",
					label: "Failed",
					value: "Failed",
					isSelected: selectedPaymentStatus === "Failed",
				},
				{
					id: "completed",
					label: "Completed",
					value: "Completed",
					isSelected: selectedPaymentStatus === "Completed",
				},
			],
			onChange: handlePaymentStatusChange,
		},
		{
			id: "billing_status",
			label: "BILLING STATUS",
			type: "radio",
			options: [
				{
					id: "notified",
					label: "Notified",
					value: "notified",
					isSelected: getBillingStatusValue() === "notified",
				},
				{
					id: "not-notified",
					label: "Not Notified",
					value: "not-notified",
					isSelected: getBillingStatusValue() === "not-notified",
				},
			],
			onChange: handleBillingStatusChange,
		},
		{
			id: "transactionType",
			label: "CHANNEL",
			type: "radio",
			options: [
				{
					id: "paymentHub",
					label: "Payment Hub",
					value: "PaymentHub",
					isSelected: selectedTransactionType === "PaymentHub",
				},
				{
					id: "CashPayment",
					label: "Cash Payment",
					value: "CashPayment",
					isSelected: selectedTransactionType === "CashPayment",
				},
			],
			onChange: handleTransactionTypeChange,
		},
	];

	const handleViewTransaction = (transaction: Transaction) => {
		if (!hasPermission(UtilityPermissions.ViewPostpaidTransactions)) {
			toast.warning("You do not have the permission for this action");
			return;
		}

		setSelectedReference(transaction.reference);
		setIsOpen(true);
	};

	const columns: Array<ColumnType<Transaction>> = [
		{
			header: "Date & Time",
			accessor: "createdAt",
			render: (record: Transaction) => <span>{formatTimestamp(record.billingDetails?.createdAt ?? "")}</span>,
		},
		{
			accessor: "billingDetails.customerName",
			header: "Name of Customer",
			render: (record: Transaction) => (
				<span>
					{record.user.firstName} {record.user.lastName}
				</span>
			),
		},
		{
			accessor: "reference",
			header: "Reference",
			render: (record: Transaction) => (
				<span title={record.reference} className={styles.ellipsisText}>
					{record.reference}
				</span>
			),
		},
		{
			accessor: "billingDetails.amount.amount",
			header: "Amount Paid",
			render: (record: Transaction) => (
				<span>{record.amount?.amount ? formatCurrency(record.amount?.amount, { currency: record.amount?.currency }) : "N/A"}</span>
			),
		},
		{
			accessor: "type",
			header: "Channel",
			render: (record: Transaction) => <span className={styles.channelBadge}>{TransactionChannelMap[record.type]}</span>,
		},
		{
			accessor: "status",
			header: "Payment Status",
			render: (record: Transaction) => (
				<span className={`${styles.statusIndicator} ${record.status ? styles[TransactionStatusMap[record.status].toLowerCase()] : ""}`}>
					{TransactionStatusMap[record.status]}
				</span>
			),
		},
		{
			accessor: "district",
			header: "District",
			render: (record: Transaction) => <span>
					 {record.billingDetails?.district ?? "N/A"}
				</span>
		},
		{
			accessor: "billingDetails.postpaidStatus",
			header: "Postpaid Status",
			render: (record: Transaction) => (
				<span
					className={`${styles.statusIndicator} ${
						record.billingDetails?.isBillingNotified ? styles[TransactionStatusMap[2].toLowerCase()] : styles[TransactionStatusMap[3].toLowerCase()]
					}`}
				>
					{record.billingDetails?.isBillingNotified ? "Notified" : "Not-Notified"}
				</span>
			),
		},
		
		{
			accessor: "id",
			header: "",
			render: (record: Transaction) => (
				<Button leftIcon={<EyeIcon className={styles.icon} />} variant="text" text="View" onClick={() => handleViewTransaction(record)} />
			),
		},
	];

	const headerActions = (
		<>
			<SearchInput initialValue={searchTerm} onSearch={(value: string) => setSearchTerm(value)} placeholder="Search by reference..." />
			<Button leftIcon={<FilterIcon2 className={styles.icon} />} text="Add Filter" variant="outline" type="button" onClick={handleOpenFilter} />
		</>
	);

	return (
		<div className={styles.historyContainer}>
			<Filter filterItems={filterItems} onApplyFilter={handleApplyFilter} onResetFilter={handleResetFilter} onClose={handleCloseFilter} isOpen={isFilterOpen} />
			{isOpen && (
				<CommonSideDialog onCloseDialog={() => setIsOpen(!isOpen)}>
					<TransactionDetailsContainer reference={selectedReference} transactionType="billing" onClose={() => setIsOpen(false)} />
				</CommonSideDialog>
			)}
			<DataTable<Transaction>
				data={data?.items ?? []}
				columns={columns}
				loading={isLoading}
				isUpdating={isFetching}
				headerActions={headerActions}
				emptyComponent={<EmptyState icon={<PostpaidActiveIcon />} title="No post paid history data" />}
			/>
			<TablePagination isLoading={isFetching} totalItems={count ?? 0} itemsPerPage={PageSize} currentPage={Page} onPageChange={setPage} />
		</div>
	);
};
